import React, { useEffect, useState } from 'react';
import { FaArrowDown, FaArrowUp, FaInfoCircle } from 'react-icons/fa';
import { BiLoaderAlt } from 'react-icons/bi';
import loanService from '../../services/api/loan.service';

// Transaction type definition based on your API response
export interface TransactionEvent {
  id: string;
  event_type: string;
  volt_customer_code: string;
  volt_customer_external_id: string;
  customer_mobile_number: string;
  user_id: string;
  event_details: {
    due_date: string | null;
    bank_name: string | null;
    due_amount: string | null;
    transfer_date: string | null;
    event_timestamp: string;
    application_type: string;
    repayment_amount: string | null;
    volt_partner_code: string | null;
    withdrawal_amount: string | null;
    account_last_4_digits: string | null;
    volt_partner_external_id: string | null;
  };
  created_at: string;
}

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<TransactionEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const response = await loanService.getLoanTransactions();
      setTransactions(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching loan transactions:', error);
      setError('Failed to load transactions. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  // Format date for better display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Function to get transaction description based on event type
  const getTransactionDescription = (transaction: TransactionEvent) => {
    const { event_type, event_details } = transaction;
    const { bank_name, account_last_4_digits, application_type } = event_details;
    
    if (event_type === 'WITHDRAWAL_INITIATED') {
      return `Withdrawal initiated from ${bank_name || 'bank'} ****${account_last_4_digits || ''}`;
    } else if (event_type === 'WITHDRAWAL_SUCCESSFUL') {
      return `Withdrawal successful to ${bank_name || 'bank'} ****${account_last_4_digits || ''}`;
    } else if (event_type.includes('REPAYMENT')) {
      return `Repayment ${event_type.replace('REPAYMENT_', '').toLowerCase().replace('_', ' ')}`;
    }
    
    return event_type.replace(/_/g, ' ').toLowerCase();
  };

  // Function to determine if transaction is credit or debit
  const isCredit = (eventType: string) => {
    return eventType.includes('REPAYMENT');
  };

  // Get amount from transaction
  const getAmount = (transaction: TransactionEvent) => {
    const { event_type, event_details } = transaction;
    
    if (event_type.includes('WITHDRAWAL')) {
      return event_details.withdrawal_amount;
    } else if (event_type.includes('REPAYMENT')) {
      return event_details.repayment_amount;
    }
    
    return '0.00';
  };

  // Skeleton loader for transaction rows
  const LoadingSkeleton = () => (
    <>
      {[1, 2, 3, 4].map((index) => (
        <tr key={index} className="animate-pulse">
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="h-4 bg-gray-200 rounded w-48"></div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="h-4 bg-gray-200 rounded w-16"></div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="h-5 bg-gray-200 rounded-full w-20"></div>
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Transaction History</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <LoadingSkeleton />
            ) : error ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center">
                  <p className="text-red-500">{error}</p>
                  <button 
                    onClick={fetchTransactions}
                    className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                  >
                    Try Again
                  </button>
                </td>
              </tr>
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  No transactions found.
                </td>
              </tr>
            ) : (
              transactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(transaction.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getTransactionDescription(transaction)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center">
                      {isCredit(transaction.event_type) ? (
                        <FaArrowDown className="text-green-500 mr-2" />
                      ) : (
                        <FaArrowUp className="text-blue-500 mr-2" />
                      )}
                      <span className={isCredit(transaction.event_type) ? 'text-green-600 font-medium' : 'text-blue-600 font-medium'}>
                        ₹{parseFloat(getAmount(transaction) || '0').toLocaleString('en-IN')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      transaction.event_type.includes('SUCCESSFUL') 
                        ? 'bg-green-100 text-green-800' 
                        : transaction.event_type.includes('INITIATED')
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {transaction.event_type.split('_').pop()?.toLowerCase()}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Loading overlay that shows when fetching data */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <BiLoaderAlt className="text-800-600 text-3xl animate-spin" />
            <p className="mt-2 text-sm text-gray-600">Loading transactions...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;